import { useMutation, useQuery } from '@tanstack/react-query';
import type { ConversationIntro } from '@raro/domain';
import { Button, Field, Input, Notice, RaroBrand, Serif } from '@raro/ui';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { api } from '../lib/api';

type Peek = { organization: string; kind: 'briefing' | 'interview'; intro: ConversationIntro | null; invitedBy: string | null };

/**
 * Link aberto de um público: a pessoa lê sobre o que é a conversa, se apresenta e ganha a própria entrevista.
 * Daqui ela segue para o link individual, que é a conversa de verdade.
 */
export function EntrevistaAbertaPage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ['conversa-aberta', token], queryFn: () => api.get<Peek>(`/conversa-aberta/${token}`), retry: false });
  const [f, setF] = useState({ name: '', role: '', relation: '' });
  const join = useMutation({ mutationFn: () => api.post<{ token: string }>(`/conversa-aberta/${token}/join`, { name: f.name.trim(), role: f.role.trim() || undefined, relation: f.relation.trim() || undefined }), onSuccess: (r) => navigate(`/conversa/${r.token}`, { replace: true }) });
  const d = q.data;
  return (
    <div className="rr-conversa">
      <div className="rr-conversa__card rr-conversa__card--open">
        <header className="rr-conversa__head">
          <RaroBrand />
          {d && <div className="rr-stack rr-stack--1" style={{ textAlign: 'right' }}><span className="rr-label">{d.organization}</span><span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>{d.kind === 'briefing' ? 'conversa de briefing' : 'conversa com a Machine'}</span></div>}
        </header>
        {q.isLoading && <p className="rr-faint">abrindo…</p>}
        {q.isError && <Notice tone="error">{(q.error as Error).message}</Notice>}
        {d && (
          <div className="rr-stack rr-stack--5">
            <div className="rr-stack rr-stack--2">
              <span className="rr-label rr-label--accent">{d.organization}{d.invitedBy ? ` · a convite de ${d.invitedBy}, da Raro` : ' · a convite da Raro'}</span>
              <h1 className="rr-display" style={{ fontSize: 'var(--rr-text-2xl)' }}><Serif italic>{d.intro?.headline ?? `uma conversa sobre a ${d.organization}`}</Serif></h1>
              <p className="rr-muted">{d.intro?.body ?? 'a Raro, pela empresa, quer ouvir você.'}</p>
              {(d.intro?.topics.length ?? 0) > 0 && (
                <div className="rr-stack rr-stack--2">
                  <span className="rr-label">vamos passar por</span>
                  <div className="rr-row rr-row--wrap" style={{ gap: 6 }}>{d.intro!.topics.map((t) => <span key={t} className="rr-chip">{t}</span>)}</div>
                </div>
              )}
              <p className="rr-conversa__fine">uns {d.intro?.minutes ?? 10} min · responda falando ou escrevendo · dá para parar quando quiser · fica com a Raro, sem terceiros</p>
            </div>
            <Field label="Como você se chama?" htmlFor="ea-n"><Input id="ea-n" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus /></Field>
            <div className="rr-row" style={{ gap: 'var(--rr-space-3)', alignItems: 'flex-start' }}>
              <Field label={d.kind === 'briefing' ? 'O que você faz na empresa' : 'O que você faz'} htmlFor="ea-r"><Input id="ea-r" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} /></Field>
              <Field label={d.kind === 'briefing' ? 'Há quanto tempo está nela' : 'Sua relação com a empresa'} htmlFor="ea-l"><Input id="ea-l" value={f.relation} onChange={(e) => setF({ ...f, relation: e.target.value })} /></Field>
            </div>
            {join.isError && <Notice tone="error">{(join.error as Error).message}</Notice>}
            <div className="rr-row"><Button variant="primary" disabled={f.name.trim().length < 2 || join.isPending} onClick={() => join.mutate()}>{join.isPending ? 'preparando…' : 'continuar'}</Button></div>
          </div>
        )}
      </div>
    </div>
  );
}
